import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { CookieConsentProvider } from "@/components/privacy/CookieConsent";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Intercept and redirect indexed malformed URLs like /https://feevho.com
function UrlNormalizer() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname.startsWith('/http')) {
      const cleanUrl = location.pathname.substring(1);
      try {
        const parsed = new URL(cleanUrl);
        if (parsed.hostname.includes('feevho.com')) {
          navigate(parsed.pathname + parsed.search + parsed.hash, { replace: true });
        } else {
          window.location.href = cleanUrl;
        }
      } catch {
        navigate('/', { replace: true });
      }
    }
  }, [location, navigate]);

  return null;
}

import Index from "./pages/Index";
import Systems from "./pages/Systems";
import SystemDetails from "./pages/SystemDetails";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import CookiePolicy from "./pages/CookiePolicy";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import VerificationPending from "./pages/auth/VerificationPending";
import PublisherGuide from "./pages/PublisherGuide";

// Articles
import Articles from "./pages/Articles";
import ArticleDetails from "./pages/ArticleDetails";

// AI Tools Pages
import AIModels from "./pages/ai-tools/AIModels";
import AIDevTools from "./pages/ai-tools/AIDevTools";
import AIMediaTools from "./pages/ai-tools/AIMediaTools";

// Partner Page
import PartnerDetails from "./pages/PartnerDetails";
import Solutions from "./pages/Solutions";

import { SessionGuard } from "@/components/auth/SessionGuard";
import { CompareProvider } from "@/contexts/CompareContext";
import { CompareBar } from "@/components/systems/CompareBar";
import { toast } from "sonner";

const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const CreateSystem = lazy(() => import("./pages/dashboard/CreateSystem"));
const SystemAnalytics = lazy(() => import("./pages/dashboard/SystemAnalytics"));
const Profile = lazy(() => import("./pages/dashboard/Profile"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const UsersList = lazy(() => import("./pages/admin/UsersList"));
const UserCreate = lazy(() => import("./pages/admin/UserCreate"));
const UserDetails = lazy(() => import("./pages/admin/UserDetails"));
const AccountLayout = lazy(() => import("./components/layout/AccountLayout"));
const AccountOverview = lazy(() => import("./pages/account/AccountOverview"));
const AccountFavorites = lazy(() => import("./pages/account/AccountFavorites"));
const AccountReviews = lazy(() => import("./pages/account/AccountReviews"));
const AccountSettings = lazy(() => import("./pages/account/AccountSettings"));
const AccountMessages = lazy(() => import("./pages/account/AccountMessages"));
const Notifications = lazy(() => import("./pages/notifications/Notifications"));
const PublisherProfile = lazy(() => import("./pages/PublisherProfile"));
const Compare = lazy(() => import("./pages/Compare"));
const CommunityFeed = lazy(() => import("./pages/Community"));
const CommunityPost = lazy(() => import("./pages/CommunityPost"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const LazyFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
  </div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<any>(undefined);
  const location = useLocation();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
  }, []);

  if (session === undefined) return <LazyFallback />;
  if (!session) {
    if (!location.state?.from) {
      setTimeout(() => toast.error("עליך להתחבר כדי לצפות בתוכן זה"), 0);
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
};

const GuestRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<any>(undefined);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
  }, []);

  if (session === undefined) return <LazyFallback />;
  if (session) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<'loading' | 'authorized' | 'unauthorized'>('loading');
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session) { setState('unauthorized'); return; }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (cancelled) return;
      if ((profile as any)?.role === 'admin' || (profile as any)?.role === 'super_admin') {
        setState('authorized');
      } else {
        setState('unauthorized');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === 'loading') return <LazyFallback />;
  if (state === 'unauthorized') {
    toast.error("אין לך הרשאה לגשת לעמוד זה");
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <BrowserRouter>
        <CompareProvider>
        <CookieConsentProvider>
          <ScrollToTop />
          <UrlNormalizer />
          <SessionGuard>
            <Suspense fallback={<LazyFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/systems" element={<Systems />} />
                
                {/* Publicly accessible System Details for SEO */}
                <Route path="/systems/:slug" element={<SystemDetails />} />
                
                <Route path="/compare" element={<Compare />} />
                <Route path="/publisher/:id" element={<PublisherProfile />} />
                <Route path="/publisher-guide" element={<PublisherGuide />} />
                
                <Route path="/community" element={<CommunityFeed />} />
                <Route path="/community/post/:id" element={<CommunityPost />} />
                
                {/* Articles / Blog */}
                <Route path="/articles" element={<Articles />} />
                <Route path="/articles/:slug" element={<ArticleDetails />} />

                {/* Partners / Integrations */}
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/partners/:slug" element={<PartnerDetails />} />

                {/* AI Tools (Protected) */}
                <Route path="/ai-models" element={<ProtectedRoute><AIModels /></ProtectedRoute>} />
                <Route path="/ai-dev-tools" element={<ProtectedRoute><AIDevTools /></ProtectedRoute>} />
                <Route path="/ai-media-tools" element={<ProtectedRoute><AIMediaTools /></ProtectedRoute>} />

                {/* Auth */}
                <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
                <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
                <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verification-pending" element={<GuestRoute><VerificationPending /></GuestRoute>} />

                {/* User Account Area (Private) */}
                <Route path="/account" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}>
                  <Route index element={<AccountOverview />} />
                  <Route path="favorites" element={<AccountFavorites />} />
                  <Route path="reviews" element={<AccountReviews />} />
                  <Route path="settings" element={<AccountSettings />} />
                  <Route path="messages" element={<AccountMessages />} />
                  <Route path="notifications" element={<Notifications />} />
                </Route>

                {/* Publisher Dashboard (Private) */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/dashboard/create" element={<ProtectedRoute><CreateSystem /></ProtectedRoute>} />
                <Route path="/dashboard/edit/:id" element={<ProtectedRoute><CreateSystem /></ProtectedRoute>} />
                <Route path="/dashboard/systems/:id/analytics" element={<ProtectedRoute><SystemAnalytics /></ProtectedRoute>} />
                <Route path="/dashboard/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

                {/* Admin */}
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><UsersList /></AdminRoute>} />
                <Route path="/admin/users/new" element={<AdminRoute><UserCreate /></AdminRoute>} />
                <Route path="/admin/users/:id" element={<AdminRoute><UserDetails /></AdminRoute>} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <CompareBar />
          </SessionGuard>
        </CookieConsentProvider>
        </CompareProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;